import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useState } from 'react'
import { InlineBanner } from '@/components/commons/inline-banner'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle
} from '@/components/ui/drawer'
import {
	AlertFillCircleIcon,
	AlertTriangleFillIcon,
	CheckCircleFillIcon,
	File05Icon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { formatDate } from '@/dates'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import {
	apiErrorMessage,
	countCsvDataRows,
	formatBytes,
	isUnchangedUpload,
	UPLOAD_REJECTION_REASONS,
	uploadRejectionToLabelKey
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import {
	type MarqueeResolutionSourceStatus,
	uploadResolutionSource
} from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** One `label ... value` line in a bordered spec list. */
function SpecRow({ label, value }: { label: string; value: ReactNode }) {
	return (
		<div className='flex items-baseline justify-between gap-4 border-grey-200 border-b py-3 last:border-b-0'>
			<span className='text-neutral-500 text-sm'>{label}</span>
			<span className='text-right font-semibold text-neutral-900'>
				{value}
			</span>
		</div>
	)
}

/**
 * `title` names what the rows are about, for a list whose figures do not speak
 * for themselves: "404 / 428" is only legible once something says these are the
 * before and after of the replacement.
 */
function SpecList({
	title,
	children
}: {
	title?: string
	children: ReactNode
}) {
	return (
		<div className='rounded-airbnb-2xl border border-grey-200 px-6 py-2'>
			{title && (
				<p className='pt-3 pb-1 font-semibold text-neutral-500 text-sm'>
					{title}
				</p>
			)}
			{children}
		</div>
	)
}

/** The picked file, with what could be read off it locally. */
function FileCard({
	file,
	detail,
	tone = 'neutral'
}: {
	file: File
	detail: string
	tone?: 'neutral' | 'danger'
}) {
	return (
		<div className='flex items-center gap-4 rounded-airbnb-2xl bg-grey-100 px-4 py-4'>
			<File05Icon className='size-8 shrink-0 text-neutral-500' />
			<div className='flex min-w-0 flex-col'>
				<span className='truncate font-medium text-neutral-900'>
					{file.name}
				</span>
				<span
					className={cn(
						'truncate text-sm',
						tone === 'danger'
							? 'text-danger-500'
							: 'text-neutral-500'
					)}
				>
					{detail}
				</span>
			</div>
		</div>
	)
}

/**
 * The shared page banner at dialog scale: same component, tighter padding, and
 * the icon carries the tone so nothing here re-implements one.
 */
function DialogNotice({
	tone,
	title,
	description
}: {
	tone: 'warning' | 'danger'
	title: string
	description: ReactNode
}) {
	const Icon = tone === 'danger' ? AlertFillCircleIcon : AlertTriangleFillIcon
	return (
		<InlineBanner
			className='p-4'
			description={description}
			icon={
				<Icon
					className={cn(
						'size-5',
						tone === 'danger'
							? 'text-danger-500'
							: 'text-orange-600'
					)}
				/>
			}
			title={title}
		/>
	)
}

interface HostLookupUploadDialogProps {
	/** The picked file. The caller remounts on a new one (see `onPickAnother`). */
	file: File
	/** Live snapshot status: the "before" figure, and the "after" provenance. */
	status: MarqueeResolutionSourceStatus | undefined
	onClose: () => void
	/** Reopens the file picker so a refused upload can be corrected in place. */
	onPickAnother: () => void
}

/**
 * The upload itself: confirm what is about to be replaced, then report what the
 * replacement actually did.
 *
 * Three phases in one shell, because they are one errand. The confirmation
 * states the replace-all semantic one last time next to the two row counts;
 * the outcome names what moved, with rows *removed* pulled out of the counter
 * list into its own warning, since those are host IDs that just stopped
 * resolving; and a refusal shows the backend's own sentence, which names the
 * headers it saw or the counts that left nothing to write.
 *
 * The caller keys this on the file, so picking another one after a refusal
 * remounts it and the mutation starts clean.
 */
export function HostLookupUploadDialog({
	file,
	status,
	onClose,
	onPickAnother
}: HostLookupUploadDialogProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')
	const queryClient = useQueryClient()

	// Read locally so the confirmation can say what the file holds before
	// anything is sent. Null until the read settles, and on a file the browser
	// cannot read at all, in which case the card falls back to size alone.
	const [dataRows, setDataRows] = useState<number | null>(null)
	useEffect(() => {
		let active = true
		file.text()
			.then((text) => {
				if (active) {
					setDataRows(countCsvDataRows(text))
				}
			})
			.catch(() => {
				if (active) {
					setDataRows(null)
				}
			})
		return () => {
			active = false
		}
	}, [file])

	const upload = useMutation({
		mutationFn: async () => {
			const response = await uploadResolutionSource({ body: { file } })
			return response.data
		},
		// Awaited, so the outcome panel reads the uploader and timestamp off a
		// refreshed status rather than inventing them client-side: the upload
		// result carries neither.
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.resolutionSource.all()
			})
		// No `onError` toast: a refusal is the dialog's own third phase, where
		// there is room for the reason. A toast would say less and vanish.
	})

	const result = upload.data
	const fileSize = formatBytes(file.size)

	const title = () => {
		if (result) {
			return t('marquee:host_lookup_upload_done_title')
		}
		return t('marquee:host_lookup_upload_title')
	}

	const body = () => {
		if (result) {
			const rejected = UPLOAD_REJECTION_REASONS.filter(
				(reason) => result[reason] > 0
			)
			return (
				<>
					<div className='flex flex-col items-center gap-2 text-center'>
						<CheckCircleFillIcon className='size-12 text-green-700' />
						<p className='font-medium text-neutral-900'>
							{t('marquee:host_lookup_upload_done_heading')}
						</p>
						<p className='text-neutral-500 text-sm'>
							{t('marquee:host_lookup_upload_done_body')}
						</p>
					</div>

					<SpecList>
						<SpecRow
							label={t('marquee:host_lookup_rows_active_label')}
							value={result.row_count}
						/>
						<SpecRow
							label={t('marquee:host_lookup_uploader_label')}
							value={
								status?.uploaded_by ??
								t('marquee:host_lookup_uploader_unknown')
							}
						/>
						<SpecRow
							label={t('marquee:host_lookup_timestamp_label')}
							value={formatDate(status?.uploaded_at, 'PPp')}
						/>
					</SpecList>

					{isUnchangedUpload(result) ? (
						<p className='text-neutral-500 text-sm'>
							{t('marquee:host_lookup_upload_no_changes')}
						</p>
					) : (
						<SpecList>
							<SpecRow
								label={t('marquee:host_lookup_rows_added')}
								value={result.rows_added}
							/>
							<SpecRow
								label={t('marquee:host_lookup_rows_removed')}
								value={result.rows_removed}
							/>
							{rejected.map((reason) => (
								<SpecRow
									key={reason}
									label={t(uploadRejectionToLabelKey[reason])}
									value={result[reason]}
								/>
							))}
						</SpecList>
					)}

					{/* The number that matters, and the only one that costs
					    something: these host IDs stopped resolving just now. */}
					{result.rows_removed > 0 && (
						<DialogNotice
							description={t('marquee:host_lookup_removed_body')}
							title={t('marquee:host_lookup_removed_title', {
								count: result.rows_removed
							})}
							tone='warning'
						/>
					)}

					{result.accepted_without_name > 0 && (
						<DialogNotice
							description={t('marquee:host_lookup_no_name_body')}
							title={t('marquee:host_lookup_no_name_title', {
								count: result.accepted_without_name
							})}
							tone='warning'
						/>
					)}
				</>
			)
		}

		if (upload.isError) {
			return (
				<>
					<FileCard
						detail={t('marquee:host_lookup_file_rejected')}
						file={file}
						tone='danger'
					/>
					<DialogNotice
						description={t('marquee:host_lookup_rejected_body')}
						title={t('marquee:host_lookup_rejected_title')}
						tone='danger'
					/>
					<div className='flex flex-col gap-2 rounded-airbnb-2xl border border-grey-200 px-6 py-5'>
						<span className='font-medium text-neutral-500 text-xs uppercase tracking-wide'>
							{t('marquee:host_lookup_errors_reported')}
						</span>
						{/* The backend's own sentence: it names the headers it
						    saw, or the rejection counts that left nothing to
						    write. No local key carries that detail. */}
						<p className='text-neutral-900 text-sm'>
							{apiErrorMessage(upload.error) ??
								t('marquee:host_lookup_rejected_unknown')}
						</p>
					</div>
				</>
			)
		}

		return (
			<>
				<FileCard
					detail={
						dataRows === null
							? fileSize
							: t('marquee:host_lookup_file_parsed', {
									count: dataRows,
									size: fileSize
								})
					}
					file={file}
				/>
				<SpecList title={t('marquee:host_lookup_what_will_change')}>
					<SpecRow
						label={t('marquee:host_lookup_current_rows')}
						value={status?.row_count ?? '-'}
					/>
					<SpecRow
						label={t('marquee:host_lookup_new_rows')}
						value={dataRows ?? '-'}
					/>
				</SpecList>
				<DialogNotice
					description={t('marquee:host_lookup_replace_body')}
					title={t('marquee:host_lookup_replace_title')}
					tone='warning'
				/>
			</>
		)
	}

	// `justify-between` is for the two-action phases, where Cancel sits opposite
	// the confirm. The outcome has a single button, which belongs at the end
	// like every other lone dialog action.
	const footerAlignment = result ? 'justify-end' : 'justify-between'

	const footer = () => {
		if (result) {
			return (
				<Button onClick={onClose} size='lg' type='button'>
					{t('marquee:host_lookup_done')}
				</Button>
			)
		}

		if (upload.isError) {
			return (
				<>
					<Button
						onClick={onClose}
						size='lg'
						type='button'
						variant='tertiary'
					>
						{t('marquee:cancel')}
					</Button>
					<Button onClick={onPickAnother} size='lg' type='button'>
						{t('marquee:host_lookup_try_another')}
					</Button>
				</>
			)
		}

		return (
			<>
				<Button
					disabled={upload.isPending}
					onClick={onClose}
					size='lg'
					type='button'
					variant='tertiary'
				>
					{t('marquee:cancel')}
				</Button>
				<Button
					disabled={upload.isPending}
					onClick={() => upload.mutate()}
					size='lg'
					type='button'
				>
					{t('marquee:host_lookup_replace_confirm')}
					{upload.isPending && <Spinner size={16} />}
				</Button>
			</>
		)
	}

	// Closing mid-flight would leave the operator with no report of an upload
	// that is still landing, so the shell only answers a dismissal once the
	// request has settled.
	const handleOpenChange = (open: boolean) => {
		if (!(open || upload.isPending)) {
			onClose()
		}
	}

	if (isDesktop) {
		return (
			<Dialog onOpenChange={handleOpenChange} open>
				<DialogContent
					className='flex max-w-[607px] flex-col'
					showCloseButton={false}
				>
					{/* The title is its own section, per the design: 60px above
					    it, 24 around the rest of it. Outside the scrolling body
					    so it stays put while a tall outcome scrolls under it. */}
					<div className='px-6 pt-15 pb-6'>
						<DialogTitle className='text-center'>
							{title()}
						</DialogTitle>
						<DialogDescription className='sr-only'>
							{t('marquee:host_lookup_upload_description')}
						</DialogDescription>
					</div>
					{/* 40px side padding, 32px below the title and between
					    blocks. `min-h-0` is what makes the body's own
					    `overflow-auto` reachable: without it the shell grows
					    past the viewport on a short window and the footer walks
					    off screen. */}
					<DialogBody className='min-h-0 flex-1 gap-8 px-10 pt-8'>
						{body()}
					</DialogBody>
					<DialogFooter
						className={cn('flex flex-row', footerAlignment)}
					>
						{footer()}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer onOpenChange={handleOpenChange} open>
			<DrawerContent>
				<DrawerHeader className='pb-2'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{title()}
					</DrawerTitle>
					<DrawerDescription className='sr-only'>
						{t('marquee:host_lookup_upload_description')}
					</DrawerDescription>
				</DrawerHeader>
				<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4'>
					{body()}
				</div>
				<DrawerFooter className={cn('flex-row', footerAlignment)}>
					{footer()}
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}
